import { ADD_ON_MODULES, MODULE_LABELS } from "./modules";
import type { ModuleName } from "./modules";

/**
 * What a clinic pays, worked out — STORY-190a, BL-D1 to BL-D16.
 *
 * **Pure and in one place**, so the operator console, the clinic's own plan view
 * and every test read one rule. Money is minor units of one platform currency
 * (BL-D3), monthly (BL-D4). Nothing here is a price: every amount arrives as a
 * row an operator entered.
 *
 * **"No price set" is not zero** (BL-D16). A line with no price has `cents:
 * null`, and **a total that depends on one is null too** rather than a figure
 * that quietly leaves something out.
 */

/** The one currency prices are held in (BL-D3), whatever a clinic charges its patients in. */
export const PLATFORM_CURRENCY = "USD";

export type PriceKind = "BASE" | "MODULE" | "BUNDLE";

/** A row of the platform's price list. Dates are `YYYY-MM-DD`, compared as text. */
export interface PriceRow {
  kind: PriceKind;
  module: ModuleName | null;
  /** `BASE` only: the band's lower edge in active branches. */
  branchesFrom: number | null;
  /** `BASE` only: the band's upper edge, null meaning "and up". */
  branchesTo: number | null;
  priceCents: number;
  effectiveFrom: string;
}

/**
 * One clinic's own price for one item (BL-D2). A `BASE` override is flat, whatever
 * the band. **A null price ends the override from its date** — back to the list —
 * so removing one never erases what it was before.
 */
export interface OverrideRow {
  kind: PriceKind;
  module: ModuleName | null;
  priceCents: number | null;
  note: string | null;
  effectiveFrom: string;
}

export type PriceSource = "OVERRIDE" | "LIST" | "UNSET";

export interface BillLine {
  /** `BASE`, `BUNDLE`, or the module it prices. */
  key: "BASE" | "BUNDLE" | ModuleName;
  label: string;
  source: PriceSource;
  /** Null when no price is set. */
  cents: number | null;
  /** The override's note, when it is one — why a clinic pays what it pays. */
  note: string | null;
}

export interface MonthlyBill {
  lines: BillLine[];
  /** Null when any line has no price: never a partial figure. */
  totalCents: number | null;
}

/** The first day of `date`'s month, as `YYYY-MM-DD`, in UTC. */
export function firstOfMonth(date: Date): string {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1))
    .toISOString()
    .slice(0, 10);
}

/** The first day of the month after `date`'s, as `YYYY-MM-DD`, in UTC (BL-D9). */
export function firstOfNextMonth(date: Date): string {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1))
    .toISOString()
    .slice(0, 10);
}

/**
 * **When a price saved today takes effect** (BL-D9): the first of next month —
 * never mid-month — except for an item that has never had a price, which starts
 * this month so that there is something to charge.
 */
export function effectiveDateFor(today: Date, hasEarlierPrice: boolean): string {
  return hasEarlierPrice ? firstOfNextMonth(today) : firstOfMonth(today);
}

/** The key one item's history is grouped under. */
const itemKeyOf = (row: {
  kind: PriceKind;
  module: ModuleName | null;
  branchesFrom?: number | null;
}): string => `${row.kind}|${row.module ?? ""}|${String(row.branchesFrom ?? 0)}`;

/**
 * The row in force on `onDate` for each item: **the latest one that has already
 * taken effect**. A price dated next month is not in force yet, so this month's
 * total is unchanged by it (BL-D9).
 */
export function inForce<
  T extends { kind: PriceKind; module: ModuleName | null; effectiveFrom: string },
>(rows: readonly T[], onDate: string, keyOf: (row: T) => string = (row) => itemKeyOf(row)): T[] {
  const best = new Map<string, T>();
  for (const row of rows) {
    if (row.effectiveFrom > onDate) continue;
    const key = keyOf(row);
    const held = best.get(key);
    if (held === undefined || row.effectiveFrom > held.effectiveFrom) best.set(key, row);
  }
  return [...best.values()];
}

/** The band price for this many active branches, or null when no band covers it. */
export function bandPrice(
  list: readonly PriceRow[],
  branchCount: number,
  onDate: string,
): number | null {
  const bands = inForce(
    list.filter((r) => r.kind === "BASE"),
    onDate,
  );
  const hit = bands.find(
    (b) =>
      b.branchesFrom !== null &&
      b.branchesFrom <= branchCount &&
      (b.branchesTo === null || branchCount <= b.branchesTo),
  );
  return hit === undefined ? null : hit.priceCents;
}

/**
 * Which add-ons are billed for the month starting `monthStart` (BL-D8): **any part
 * of a month counts in full, and switching one off takes effect from next month's
 * bill.** So a module is billed if it is on now, or if it was switched off during
 * this month or later — it was on for some of it. One never switched on, or
 * switched off before the month began, is not.
 *
 * The record keeps only the latest change, so this answers the current month and
 * the next; it cannot rebuild an older month once the module has changed again.
 */
export function billedModules(
  modules: readonly { module: ModuleName; enabled: boolean; changedAt: string }[],
  monthStart: string,
): ModuleName[] {
  return ADD_ON_MODULES.filter((module) => {
    const row = modules.find((m) => m.module === module);
    if (row === undefined) return false;
    return row.enabled || row.changedAt.slice(0, 10) >= monthStart;
  });
}

/**
 * **What a clinic is charged for the month containing `onDate`** (BL-D2, BL-D5,
 * BL-D6, BL-D8, BL-D9, BL-D16).
 *
 * - The base is the clinic's own flat override if it has one, otherwise the band
 *   its active branch count falls in.
 * - **A clinic billed for all three add-ons pays the bundle instead of the
 *   three**, when a bundle price exists (its own override first, then the list);
 *   with none, it pays each add-on's price.
 * - Each add-on is the clinic's override, otherwise the list.
 * - **Any line with no price makes the total null.**
 */
export function monthlyBill(input: {
  branchCount: number;
  billed: readonly ModuleName[];
  list: readonly PriceRow[];
  overrides: readonly OverrideRow[];
  onDate: string;
}): MonthlyBill {
  const { branchCount, billed, onDate } = input;
  const overrides = inForce(input.overrides, onDate);
  const listed = inForce(input.list, onDate);

  const overrideOf = (kind: PriceKind, module: ModuleName | null): OverrideRow | undefined =>
    overrides.find((o) => o.kind === kind && o.module === module && o.priceCents !== null);

  const priced = (
    key: BillLine["key"],
    label: string,
    override: OverrideRow | undefined,
    fromList: number | null,
  ): BillLine =>
    override !== undefined
      ? { key, label, source: "OVERRIDE", cents: override.priceCents ?? 0, note: override.note }
      : fromList !== null
        ? { key, label, source: "LIST", cents: fromList, note: null }
        : { key, label, source: "UNSET", cents: null, note: null };

  const lines: BillLine[] = [
    priced(
      "BASE",
      "Base plan",
      overrideOf("BASE", null),
      bandPrice(input.list, branchCount, onDate),
    ),
  ];

  const bundleOverride = overrideOf("BUNDLE", null);
  const bundleList = listed.find((r) => r.kind === "BUNDLE")?.priceCents ?? null;
  const allThree = ADD_ON_MODULES.every((m) => billed.includes(m));

  if (allThree && (bundleOverride !== undefined || bundleList !== null)) {
    lines.push(priced("BUNDLE", "Everything bundle", bundleOverride, bundleList));
  } else {
    for (const module of ADD_ON_MODULES) {
      if (!billed.includes(module)) continue;
      const fromList = listed.find((r) => r.kind === "MODULE" && r.module === module)?.priceCents;
      lines.push(
        priced(module, MODULE_LABELS[module], overrideOf("MODULE", module), fromList ?? null),
      );
    }
  }

  const totalCents = lines.some((l) => l.cents === null)
    ? null
    : lines.reduce((sum, l) => sum + (l.cents ?? 0), 0);
  return { lines, totalCents };
}

/**
 * Whether a set of bands is one the base can be priced by: starting at one
 * branch, each band beginning where the last ended, and the last open-ended. A
 * gap would leave a clinic with a branch count nobody priced.
 */
export function bandsProblem(
  bands: readonly { branchesFrom: number; branchesTo: number | null }[],
): string | null {
  if (bands.length === 0) return "Add at least one band";
  const sorted = [...bands].sort((a, b) => a.branchesFrom - b.branchesFrom);
  if (sorted[0]?.branchesFrom !== 1) return "The first band must start at one branch";
  for (let i = 0; i < sorted.length; i += 1) {
    const band = sorted[i];
    const next = sorted[i + 1];
    if (band === undefined) continue;
    if (band.branchesTo !== null && band.branchesTo < band.branchesFrom) {
      return "A band cannot end before it starts";
    }
    if (next === undefined) {
      if (band.branchesTo !== null) return "The last band must have no upper limit";
    } else if (band.branchesTo === null || next.branchesFrom !== band.branchesTo + 1) {
      return "Each band must begin where the one before it ended, with no gap or overlap";
    }
  }
  return null;
}
